import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './DeliveryMap.css';

// Fix for default marker icons in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Location {
  lat: number;
  lng: number;
  name: string;
  address?: string;
}

interface DeliveryMapProps {
  pickupLocation: Location;
  deliveryLocation: Location;
  orderStatus: string;
  estimatedDelivery?: string;
}

// Helper function to calculate distance between two points (Haversine formula)
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Helper function to calculate bearing
const calculateBearing = (start: Location, end: Location): number => {
  const startLat = start.lat * Math.PI / 180;
  const startLng = start.lng * Math.PI / 180;
  const endLat = end.lat * Math.PI / 180;
  const endLng = end.lng * Math.PI / 180;
  
  const dLng = endLng - startLng;
  const y = Math.sin(dLng) * Math.cos(endLat);
  const x = Math.cos(startLat) * Math.sin(endLat) -
            Math.sin(startLat) * Math.cos(endLat) * Math.cos(dLng);
  let bearing = Math.atan2(y, x) * 180 / Math.PI;
  bearing = (bearing + 360) % 360;
  
  return bearing;
};

// Get location based on order status
const getCurrentPosition = (
  pickupLocation: Location, 
  deliveryLocation: Location, 
  orderStatus: string,
  progress: number = 0.5
): Location => {
  switch (orderStatus) {
    case 'pending':
    case 'confirmed':
      return pickupLocation; 
    case 'processing':
      // 25% of the way
      return {
        lat: pickupLocation.lat + (deliveryLocation.lat - pickupLocation.lat) * 0.25,
        lng: pickupLocation.lng + (deliveryLocation.lng - pickupLocation.lng) * 0.25,
        name: 'Processing at Warehouse'
      };
    case 'shipped':
      // 75% of the way based on progress
      return {
        lat: pickupLocation.lat + (deliveryLocation.lat - pickupLocation.lat) * progress,
        lng: pickupLocation.lng + (deliveryLocation.lng - pickupLocation.lng) * progress,
        name: 'In Transit'
      };
    case 'delivered':
      return deliveryLocation;
    default:
      return pickupLocation;
  }
};

const DeliveryMap: React.FC<DeliveryMapProps> = ({ 
  pickupLocation, 
  deliveryLocation, 
  orderStatus,
  estimatedDelivery
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const vanMarkerRef = useRef<L.Marker | null>(null);
  const [progress, setProgress] = useState(0.5);
  const [distance, setDistance] = useState<number | null>(null);

  // Calculate distance when locations change
  useEffect(() => {
    if (pickupLocation && deliveryLocation) {
      const dist = calculateDistance(
        pickupLocation.lat, pickupLocation.lng,
        deliveryLocation.lat, deliveryLocation.lng
      );
      setDistance(dist);
    }
  }, [pickupLocation, deliveryLocation]);

  // Animate van movement for shipped status
  useEffect(() => {
    if (orderStatus === 'shipped') {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 0.95) {
            clearInterval(interval);
            return 0.95;
          }
          return prev + 0.01;
        });
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setProgress(0.5);
    }
  }, [orderStatus]);

  // Create custom van icon with rotation
  const createVanIcon = (
    bearing: number,
    status: string
  ): L.DivIcon => {
    const vanColor =
      status === 'delivered' ? '#4caf50' : '#ff6b35';

    const canvas = document.createElement('canvas');
    canvas.width = 60;
    canvas.height = 60;

    const ctx = canvas.getContext('2d');

    if (ctx) {
      ctx.clearRect(0, 0, 60, 60);

      ctx.save();

      // Move rotation point to the center of the icon.
      ctx.translate(30, 30);

      /*
      * Leaflet bearing:
      *
      * 0°   = North
      * 90°  = East
      * 180° = South
      * 270° = West
      *
      * The van below is drawn facing UP,
      * so no 90° correction is required.
      */
      ctx.rotate((bearing * Math.PI) / 180);

      /*
      * -------------------------
      * VAN BODY
      * -------------------------
      */

      // Main van body
      ctx.fillStyle = vanColor;
      ctx.beginPath();
      ctx.roundRect(-11, -17, 22, 34, 4);
      ctx.fill();

      /*
      * -------------------------
      * FRONT / CAB
      * -------------------------
      *
      * The front is at the TOP.
      */
      ctx.fillStyle = '#333';

      ctx.beginPath();
      ctx.roundRect(-11, -17, 22, 10, 3);
      ctx.fill();

      /*
      * -------------------------
      * WINDSHIELD
      * -------------------------
      */
      ctx.fillStyle = '#87CEEB';

      ctx.beginPath();
      ctx.roundRect(-7, -14, 14, 5, 1);
      ctx.fill();

      /*
      * -------------------------
      * HEADLIGHTS
      * -------------------------
      */
      ctx.fillStyle = '#ffeb3b';

      ctx.fillRect(-9, -18, 5, 3);
      ctx.fillRect(4, -18, 5, 3);

      /*
      * -------------------------
      * SIDE WINDOWS
      * -------------------------
      */
      ctx.fillStyle = '#87CEEB';

      ctx.fillRect(-8, -3, 16, 7);

      /*
      * -------------------------
      * VAN DOOR / BODY DETAIL
      * -------------------------
      */
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.25)';
      ctx.lineWidth = 1;

      ctx.beginPath();
      ctx.moveTo(0, 5);
      ctx.lineTo(0, 14);
      ctx.stroke();

      /*
      * -------------------------
      * WHEELS
      * -------------------------
      */
      ctx.fillStyle = '#222';

      ctx.beginPath();
      ctx.arc(-12, -8, 4, 0, Math.PI * 2);
      ctx.arc(12, -8, 4, 0, Math.PI * 2);
      ctx.arc(-12, 9, 4, 0, Math.PI * 2);
      ctx.arc(12, 9, 4, 0, Math.PI * 2);
      ctx.fill();

      /*
      * Restore canvas state.
      */
      ctx.restore();
    }

    return L.divIcon({
      html: `
        <img
          src="${canvas.toDataURL()}"
          style="
            width: 60px;
            height: 60px;
            display: block;
          "
          alt="Delivery van"
        />
      `,
      iconSize: [60, 60],
      iconAnchor: [30, 30],
      className: 'van-marker',
    });
  };

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current).setView(
      [pickupLocation.lat, pickupLocation.lng], 
      12
    );
    
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);
    
    mapRef.current = map;

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [pickupLocation]);

  useEffect(() => {
    if (!mapRef.current) return;
    
    const map = mapRef.current;
    const currentPos = getCurrentPosition(pickupLocation, deliveryLocation, orderStatus, progress);
    const bearing = calculateBearing(currentPos, deliveryLocation);
    
    // Clear existing layers properly
    map.eachLayer((layer: L.Layer) => {
      if (layer instanceof L.Marker || layer instanceof L.Polyline) {
        map.removeLayer(layer);
      }
    });
    
    // Add pickup marker with custom icon
    const pickupIcon = L.divIcon({
      html: '🏢',
      iconSize: [30, 30],
      className: 'pickup-marker'
    });
    
    L.marker([pickupLocation.lat, pickupLocation.lng], { icon: pickupIcon })
      .bindPopup(`
        <div class="popup-content">
          <strong>📦 Pickup Location</strong><br/>
          <strong>${pickupLocation.name}</strong><br/>
          ${pickupLocation.address || ''}<br/>
          <small>${pickupLocation.lat.toFixed(4)}, ${pickupLocation.lng.toFixed(4)}</small>
          ${distance ? `<br/><small>📏 Distance: ${distance.toFixed(1)} km away</small>` : ''}
        </div>
      `)
      .addTo(map);
    
    // Add delivery marker with custom icon
    const deliveryIcon = L.divIcon({
      html: '📍',
      iconSize: [30, 30],
      className: 'delivery-marker'
    });
    
    L.marker([deliveryLocation.lat, deliveryLocation.lng], { icon: deliveryIcon })
      .bindPopup(`
        <div class="popup-content">
          <strong>🎯 Delivery Location</strong><br/>
          <strong>${deliveryLocation.name}</strong><br/>
          ${deliveryLocation.address || ''}<br/>
          <small>${deliveryLocation.lat.toFixed(4)}, ${deliveryLocation.lng.toFixed(4)}</small>
          ${distance ? `<br/><small>📏 Distance: ${distance.toFixed(1)} km from warehouse</small>` : ''}
        </div>
      `)
      .addTo(map);
    
    // Add van marker (current position)
    const vanIcon = createVanIcon(bearing, orderStatus);
    
    const vanMarker = L.marker([currentPos.lat, currentPos.lng], { icon: vanIcon })
      .bindPopup(`
        <div class="popup-content">
          <strong>🚚 Delivery Van</strong><br/>
          Status: ${orderStatus.toUpperCase()}<br/>
          ${orderStatus === 'shipped' ? `Heading to ${deliveryLocation.name}!` : 'Preparing for delivery'}<br/>
          ${orderStatus === 'shipped' && distance ? `~${(distance * (1 - progress)).toFixed(1)} km remaining` : ''}
        </div>
      `);
    
    vanMarker.addTo(map);
    vanMarkerRef.current = vanMarker;
    
    // Draw route line between pickup and delivery
    const routePoints: L.LatLngExpression[] = [
      [pickupLocation.lat, pickupLocation.lng],
      [deliveryLocation.lat, deliveryLocation.lng]
    ];
    
    L.polyline(routePoints, {
      color: '#ff6b35',
      weight: 4,
      opacity: 0.8,
      dashArray: '10, 10'
    }).addTo(map);
    
    // Add distance marker at midpoint
    const midPoint = {
      lat: (pickupLocation.lat + deliveryLocation.lat) / 2,
      lng: (pickupLocation.lng + deliveryLocation.lng) / 2
    };
    
    if (distance) {
      const distanceIcon = L.divIcon({
        html: `<div style="background: white; padding: 4px 8px; border-radius: 20px; font-size: 12px; font-weight: bold; border: 1px solid #ff6b35;">📏 ${distance.toFixed(1)} km</div>`,
        iconSize: [80, 30],
        className: 'distance-marker'
      });
      
      L.marker([midPoint.lat, midPoint.lng], { icon: distanceIcon }).addTo(map);
    }
    
    // Fit bounds to show entire route
    const bounds = L.latLngBounds(routePoints);
    map.fitBounds(bounds, { padding: [50, 50] });
    
  }, [pickupLocation, deliveryLocation, orderStatus, progress, distance]);

  const getStatusMessage = (): string => {
    switch (orderStatus) {
      case 'pending':
        return '⏳ Order pending payment confirmation';
      case 'confirmed':
        return '✅ Order confirmed! Preparing for pickup from Zaria warehouse';
      case 'processing':
        return '⚙️ Order being processed at Zaria warehouse';
      case 'shipped':
        return `🚚 Your order is on the way from Zaria! ${distance ? `~${(distance * 0.75).toFixed(1)} km remaining` : ''}`;
      case 'delivered':
        return '🎉 Order delivered! Thank you for shopping with us!';
      default:
        return 'Processing your order';
    }
  };

  return (
    <div className="delivery-map-container">
      <div className="map-header">
        <h3>📍 Delivery Tracking</h3>
        <div className="status-banner" style={{
          backgroundColor: orderStatus === 'delivered' ? '#4caf50' : '#ff6b35'
        }}>
          {getStatusMessage()}
        </div>
      </div>
      <div ref={mapContainerRef} className="delivery-map" />
      <div className="map-footer">
        <div className="legend">
          <div className="legend-item">
            <span className="legend-dot pickup"></span>
            <span>GVS Warehouse (Zaria, Kaduna)</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot delivery"></span>
            <span>Your Delivery Location</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot route"></span>
            <span>Delivery Route</span>
          </div>
          <div className="legend-item">
            <span className="legend-icon">🚚</span>
            <span>Delivery Van (rotates toward you)</span>
          </div>
        </div>
        {estimatedDelivery && (
          <div className="estimated-delivery">
            📅 Estimated Delivery: {estimatedDelivery}
          </div>
        )}
        <div className="map-note">
          💡 Van icon rotates to point toward your delivery location
        </div>
      </div>
    </div>
  );
};

export default DeliveryMap;